/**
 * QA Panel — Jira tab controller.
 *
 * Read-only inspection of the Jira tickets included in the current build.
 * The data is build-time static — a JSON string literal injected by webpack
 * DefinePlugin from `config/buildTickets.js`. The controller parses it once
 * at construction and never mutates it.
 *
 * Because the data never changes after boot, there are no subscriptions and
 * no polling — `init` just stores the `onChange` callback (it will never
 * fire). Matches the controller-shape contract used by the rest of the panel
 * so the bridge can wire it the same way as the other tabs.
 *
 * Build-flag discipline: `process.env.QA_BUILD_TICKETS` is read here and
 * here only. This file lives under `features/qaPanel/`, which is dead-
 * stripped in non-allowlisted builds, so the literal string lives and dies
 * with the panel. Do NOT read the env var from anywhere outside qaPanel.
 *
 * File-area aggregation (top-level path bucketing for the "hot areas" strip
 * in the Jira tab header) is computed here, NOT in the build-time
 * extractor — keeps the injected JSON lean and lets the popup display
 * evolve without re-running the build.
 */

/** One commit reference within a BuildTicket — hash plus optional PR id. */
export interface BuildTicketCommit {
    /** Short commit hash as emitted by `git log --format=%h`. */
    hash: string;
    /** Bitbucket PR number, if extractable from the commit subject/body. */
    pr: number | null;
}

/** One commit author for a ticket — display name plus the git email. */
export interface BuildTicketAuthor {
    /** Display name (`%an` from git log). */
    name: string;
    /**
     * Author email (`%ae` from git log). Used as the Teams 1:1 deep-link
     * target — also doubles as the dedupe key at extraction time. Will be an
     * empty string if the payload came from a pre-email build (back-compat
     * parser branch); empty-email entries render as plain text downstream.
     */
    email: string;
}

/** One Jira ticket key referenced by one or more commit subjects in the build. */
export interface BuildTicket {
    /** Ticket key in the form "ON-<digits>". */
    key: string;
    /** Commit subjects that mentioned the key. May contain duplicates by design — display joins them. */
    subjects: string[];
    /** Commits parallel to `subjects`, with optional PR ids for inline links. */
    commits: BuildTicketCommit[];
    /**
     * Files changed by this ticket's commits, deduped + sorted alphabetically.
     * Lockfiles are filtered out at extraction time. Empty for merge-only
     * tickets or when the build metadata is absent (forward-compat default).
     */
    files: string[];
    /**
     * Unique commit authors for this ticket. Deduped by email at extraction
     * time, sorted alphabetically by display name. Non-personal addresses
     * (CI/service accounts) are filtered out — see PERSONAL_EMAIL_RE in
     * `config/buildTickets.js`.
     */
    authors: BuildTicketAuthor[];
    /**
     * 'new' when the ticket appeared first in the current rc, 'carried' when
     * it was also present in the prior rc, undefined when the comparison is
     * impossible (no prev-prev bump found — shallow clone, first release).
     */
    status?: 'new' | 'carried';
}

/** One "hot area" entry — derived in the controller from BuildTicket.files. */
export interface JiraFileArea {
    /** Display label, e.g. "features/games", "core", "store", "other". */
    label: string;
    /** Distinct files contributing to this bucket across all tickets. */
    count: number;
}

export interface JiraTabState {
    /** Most recent prior version-bump (e.g. "v2.7.0-rc.4"), or null if extraction failed. */
    previousVersion: string | null;
    /** Tickets referenced by commits between previousVersion and HEAD. */
    tickets: BuildTicket[];
    /** Top file-churn areas across all tickets, sorted desc, capped to 6. */
    fileAreas: JiraFileArea[];
}

interface RawPayload {
    previousVersion?: unknown;
    tickets?: unknown;
}

interface RawCommit {
    hash?: unknown;
    pr?: unknown;
}

interface RawAuthor {
    name?: unknown;
    email?: unknown;
}

const FILE_AREA_LIMIT = 6;

function parseCommit(raw: unknown): BuildTicketCommit | null {
    // Back-compat: older payloads stored commits as plain string hashes.
    // We still accept them so a stale build artifact doesn't crash the tab.
    if (typeof raw === 'string') {
        return { hash: raw, pr: null };
    }
    if (!raw || typeof raw !== 'object') return null;
    const r = raw as RawCommit;
    if (typeof r.hash !== 'string') return null;
    const pr = typeof r.pr === 'number' && Number.isFinite(r.pr) ? r.pr : null;
    return { hash: r.hash, pr };
}

function parseStatus(raw: unknown): 'new' | 'carried' | undefined {
    if (raw === 'new' || raw === 'carried') return raw;
    return undefined;
}

function parseAuthor(raw: unknown): BuildTicketAuthor | null {
    // Back-compat: older payloads stored authors as plain name strings (no
    // email available). Map them to the new shape with an empty email — the
    // popup will then render the name as plain text instead of a Teams link.
    if (typeof raw === 'string') {
        return { name: raw, email: '' };
    }
    if (!raw || typeof raw !== 'object') return null;
    const r = raw as RawAuthor;
    if (typeof r.name !== 'string') return null;
    const email = typeof r.email === 'string' ? r.email : '';
    return { name: r.name, email };
}

function parsePayload(raw: string | undefined): { previousVersion: string | null; tickets: BuildTicket[] } {
    if (!raw) return { previousVersion: null, tickets: [] };
    try {
        const parsed = JSON.parse(raw) as RawPayload;
        const previousVersion =
            typeof parsed.previousVersion === 'string' ? parsed.previousVersion : null;
        const ticketsRaw = Array.isArray(parsed.tickets) ? parsed.tickets : [];
        const tickets: BuildTicket[] = [];
        ticketsRaw.forEach((entry) => {
            if (!entry || typeof entry !== 'object') return;
            const e = entry as {
                key?: unknown;
                subjects?: unknown;
                commits?: unknown;
                files?: unknown;
                authors?: unknown;
                status?: unknown;
            };
            if (typeof e.key !== 'string') return;
            const subjects = Array.isArray(e.subjects)
                ? e.subjects.filter((s): s is string => typeof s === 'string')
                : [];
            const commits = Array.isArray(e.commits)
                ? e.commits
                      .map(parseCommit)
                      .filter((c): c is BuildTicketCommit => c !== null)
                : [];
            // `files` defaults to [] for forward/back-compat with payloads
            // emitted before file extraction was added.
            const files = Array.isArray(e.files)
                ? e.files.filter((f): f is string => typeof f === 'string')
                : [];
            const authors = Array.isArray(e.authors)
                ? e.authors
                      .map(parseAuthor)
                      .filter((a): a is BuildTicketAuthor => a !== null)
                : [];
            const ticket: BuildTicket = { key: e.key, subjects, commits, files, authors };
            const status = parseStatus(e.status);
            if (status) ticket.status = status;
            tickets.push(ticket);
        });
        return { previousVersion, tickets };
    } catch {
        return { previousVersion: null, tickets: [] };
    }
}

/**
 * Bucket a file path by its top-level segment of interest. Returns null for
 * paths that are too low-signal to chart (root-level configs, repo metadata)
 * — caller drops them.
 */
function bucketFilePath(filePath: string): string | null {
    const featureMatch = filePath.match(/^apps\/lobby2\.0\/src\/features\/([^/]+)\//);
    if (featureMatch) return 'features/' + featureMatch[1];
    if (filePath.startsWith('apps/lobby2.0/src/core/')) return 'core';
    if (filePath.startsWith('apps/lobby2.0/src/store/')) return 'store';
    if (filePath.startsWith('apps/lobby2.0/src/shared/')) return 'shared';
    if (filePath.startsWith('apps/lobby2.0/src/layouts/')) return 'layouts';
    if (filePath.startsWith('apps/lobby2.0/config/')) return 'config';
    return 'other';
}

/**
 * Aggregate file-churn buckets across all tickets. Each file is counted
 * once per bucket regardless of how many tickets touched it — gives a
 * "where did this rc move?" overview rather than per-ticket weighting.
 */
function computeFileAreas(tickets: BuildTicket[]): JiraFileArea[] {
    const perBucket = new Map<string, Set<string>>();
    tickets.forEach((t) => {
        t.files.forEach((f) => {
            const bucket = bucketFilePath(f);
            if (!bucket) return;
            let set = perBucket.get(bucket);
            if (!set) {
                set = new Set();
                perBucket.set(bucket, set);
            }
            set.add(f);
        });
    });
    const entries: JiraFileArea[] = [];
    perBucket.forEach((set, label) => {
        entries.push({ label, count: set.size });
    });
    entries.sort((a, b) => {
        if (b.count !== a.count) return b.count - a.count;
        return a.label.localeCompare(b.label);
    });
    return entries.slice(0, FILE_AREA_LIMIT);
}

export class JiraController {
    private readonly previousVersion: string | null;
    private readonly tickets: BuildTicket[];
    private readonly fileAreas: JiraFileArea[];
    private onChange: (() => void) | null = null;

    constructor() {
        // The DefinePlugin injection turns this access into a literal string
        // at build time. Read once at construction — the data never changes.
        const parsed = parsePayload(process.env['QA_BUILD_TICKETS']);
        this.previousVersion = parsed.previousVersion;
        this.tickets = parsed.tickets;
        this.fileAreas = computeFileAreas(parsed.tickets);
    }

    init(onChange: () => void): void {
        // No subscriptions or polling — build metadata is static. Hold the
        // callback to match the controller contract; it will never fire.
        this.onChange = onChange;
    }

    destroy(): void {
        this.onChange = null;
    }

    getState(): JiraTabState {
        // Return a fresh object reference each call so consumers that compare
        // by reference still see "new" data — matches the immutable-snapshot
        // pattern used by every other controller in the panel.
        return {
            previousVersion: this.previousVersion,
            tickets: this.tickets.map((t) => {
                const out: BuildTicket = {
                    key: t.key,
                    subjects: t.subjects.slice(),
                    commits: t.commits.map((c) => ({ hash: c.hash, pr: c.pr })),
                    files: t.files.slice(),
                    authors: t.authors.map((a) => ({ name: a.name, email: a.email })),
                };
                if (t.status) out.status = t.status;
                return out;
            }),
            fileAreas: this.fileAreas.map((a) => ({ label: a.label, count: a.count })),
        };
    }
}
